import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Paperclip, X } from 'lucide-react';
import { Button } from '../../../core/components/Button';
import { useRestClient } from '../../../core/api/RestClientContext';
import { AttachmentDto, ChatDto, ChatStatus, MessageDto } from '../models/support';
import './ChatConversation.css';

interface ChatConversationProps {
    chat: ChatDto | null;
    sending: boolean;
    onSendMessage: (tgId: string, text: string, file: File | null) => void;
    onChangeStatus: (tgId: string, status: ChatStatus) => void;
}

const ACCEPTED_MIME = 'image/jpeg,image/png,image/webp,image/gif';

function formatTime(iso: string): string {
    return new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

function isImage(att: AttachmentDto): boolean {
    return att.mimeType?.startsWith('image/') ?? false;
}

export const ChatConversation: React.FC<ChatConversationProps> = ({
    chat,
    sending,
    onSendMessage,
    onChangeStatus,
}) => {
    const restClient = useRestClient();
    const [text, setText] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const bottomRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const filePreviewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
    useEffect(() => () => {
        if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
    }, [filePreviewUrl]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [chat?.messages.length]);

    if (!chat) {
        return (
            <div className="chat-conv chat-conv--empty">
                <span className="chat-conv__placeholder">Выберите чат</span>
            </div>
        );
    }

    const isClosed = chat.status === 'CLOSED';

    const attachmentUrl = (tgId: string, att: AttachmentDto): string =>
        restClient.buildUrl(`/support/chats/${tgId}/files/${encodeURIComponent(att.fileName)}`);

    const resetInput = () => {
        setText('');
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleSend = () => {
        const trimmed = text.trim();
        if (!trimmed && !file) return;
        if (sending) return;
        onSendMessage(chat.tgId, trimmed, file);
        resetInput();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const picked = e.target.files?.[0] ?? null;
        setFile(picked);
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        const imageItem = Array.from(e.clipboardData.items).find(i => i.type.startsWith('image/'));
        if (!imageItem) return;
        const pasted = imageItem.getAsFile();
        if (pasted) {
            e.preventDefault();
            setFile(pasted);
        }
    };

    const renderAttachment = (msg: MessageDto) => {
        if (!msg.attachment) return null;
        const url = attachmentUrl(chat.tgId, msg.attachment);
        if (isImage(msg.attachment)) {
            return (
                <a href={url} target="_blank" rel="noopener noreferrer" className="chat-conv__image-link">
                    <img src={url} alt={msg.attachment.fileName} className="chat-conv__image" />
                </a>
            );
        }
        return (
            <a href={url} target="_blank" rel="noopener noreferrer" className="chat-conv__file-link">
                📎 {msg.attachment.fileName}
            </a>
        );
    };

    return (
        <div className="chat-conv">
            <div className="chat-conv__header">
                <span className="chat-conv__title">@{chat.username}</span>
                <div className="chat-conv__actions">
                    {chat.status === 'WAITING_HUMAN' && (
                        <Button size="sm" variant="primary"
                            onClick={() => onChangeStatus(chat.tgId, 'HUMAN')}>
                            Взять чат
                        </Button>
                    )}
                    {(chat.status === 'HUMAN' || chat.status === 'AUTO') && (
                        <Button size="sm" variant="secondary"
                            onClick={() => onChangeStatus(chat.tgId, 'CLOSED')}>
                            Закрыть чат
                        </Button>
                    )}
                </div>
            </div>

            <div className="chat-conv__messages">
                {chat.messages.map((msg, idx) => (
                    <div
                        key={idx}
                        className={`chat-conv__msg chat-conv__msg--${msg.role.toLowerCase()}`}
                    >
                        <div className="chat-conv__bubble">
                            <span className="chat-conv__role">
                                {msg.role === 'USER' ? `@${chat.username}` : msg.role}
                            </span>
                            {renderAttachment(msg)}
                            {msg.content && <span className="chat-conv__content">{msg.content}</span>}
                            <span className="chat-conv__time">{formatTime(msg.timestamp)}</span>
                        </div>
                    </div>
                ))}
                <div ref={bottomRef} />
            </div>

            {!isClosed && (
                <div className="chat-conv__input-row">
                    {file && (
                        <div className="chat-conv__file-preview">
                            {filePreviewUrl && file.type.startsWith('image/') ? (
                                <img src={filePreviewUrl} alt="preview" />
                            ) : (
                                <span>📎 {file.name}</span>
                            )}
                            <button
                                type="button"
                                className="chat-conv__file-remove"
                                onClick={() => setFile(null)}
                                aria-label="Убрать файл"
                            >
                                <X size={14} />
                            </button>
                        </div>
                    )}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPTED_MIME}
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                    />
                    <button
                        type="button"
                        className="chat-conv__attach-btn"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={sending}
                        title="Прикрепить картинку"
                    >
                        <Paperclip size={18} />
                    </button>
                    <textarea
                        className="chat-conv__textarea"
                        placeholder="Введите сообщение или вставьте картинку..."
                        value={text}
                        onChange={e => setText(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                        rows={2}
                        disabled={sending}
                    />
                    <Button
                        size="sm"
                        variant="primary"
                        onClick={handleSend}
                        disabled={(!text.trim() && !file) || sending}
                    >
                        Отправить
                    </Button>
                </div>
            )}
        </div>
    );
};
